import { Dispatch } from 'redux';
import getHomeSpaces from '@core/api/spaces/getHomeSpaces';
import getHomeSpacesProgress from '@core/api/materials/getHomeSpacesProgress';
import getHomeSpacesEngagement from '@core/api/engagement/getHomeSpacesEngagement';
import {
  HOME_FETCH_SPACES_FAILURE,
  HOME_FETCH_SPACES_REQUEST,
  HOME_FETCH_SPACES_SUCCESS,
  HOME_REFRESH_SPACES_CARD_DATA_FAILURE,
  HOME_REFRESH_SPACES_CARD_DATA_SUCCESS,
} from '@core/constants/actions';
import { getHomeViewRole } from '@app/selectors/home';

/** First page size — keep small so the carousel can paint quickly. */
export const HOME_SPACES_PAGE_SIZE = 10;

const getHomeSpacesErrorType = (error: unknown): 'network' | 'api' => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'network';
  }

  const message = error instanceof Error ? error.message : String(error ?? '');

  if (
    message === 'Network Error' ||
    message.includes('Failed to fetch') ||
    message.includes('Network request failed')
  ) {
    return 'network';
  }

  return 'api';
};

export const fetchHomeSpaces =
  () => async (dispatch: Dispatch, getState: () => any) => {
    const state = getState();
    const viewRole = getHomeViewRole(state);
    const roleState = state.home.spacesByRole[viewRole];

    if (roleState.loading || !roleState.hasMore) {
      return;
    }

    dispatch({ type: HOME_FETCH_SPACES_REQUEST, role: viewRole });

    try {
      const { homeSpaces, nextCursor } = await getHomeSpaces({
        limit: HOME_SPACES_PAGE_SIZE,
        cursor: roleState.cursor,
        role: viewRole,
      });

      // Render space cards as soon as the list arrives; metrics fill in after.
      // Only the newly fetched cards should show metrics skeletons.
      const loadingCardMetricsSpaceIds = homeSpaces.map(
        (space) => space.spaceId
      );

      dispatch({
        type: HOME_FETCH_SPACES_SUCCESS,
        role: viewRole,
        spaces: homeSpaces,
        progress: [],
        engagement: [],
        cursor: nextCursor,
        hasMore: nextCursor !== null,
        loadingCardMetricsSpaceIds,
      });

      if (homeSpaces.length === 0) {
        return;
      }

      const spaceIds = loadingCardMetricsSpaceIds;
      const educatorIds = homeSpaces
        .filter((space) => space.role === 'educator')
        .map((space) => space.spaceId);

      try {
        const [progressResponse, engagementResponse] = await Promise.all([
          getHomeSpacesProgress(spaceIds),
          getHomeSpacesEngagement(educatorIds),
        ]);

        dispatch({
          type: HOME_REFRESH_SPACES_CARD_DATA_SUCCESS,
          role: viewRole,
          progress: progressResponse.spaceProgress,
          engagement: engagementResponse.spaceEngagement,
        });
      } catch (metricsError) {
        console.error('Failed to fetch home space card metrics', metricsError);
        dispatch({
          type: HOME_REFRESH_SPACES_CARD_DATA_FAILURE,
          role: viewRole,
          spaceId: spaceIds[0],
        });
      }
    } catch (error) {
      console.error('Failed to fetch home spaces', error);
      dispatch({
        type: HOME_FETCH_SPACES_FAILURE,
        role: viewRole,
        errorType: getHomeSpacesErrorType(error),
      });
    }
  };
